import type { PlaycastPeerClientType, PlaycastPeerHostType, PlaycastTrackSet } from '@playcastdotio/CoreTypes';
import { createHostId, createPlayerPeerId, createRemoteControlClientId, createRemoteHostId } from '@playcastdotio/CreateId';

export const getAcceptedClientPeerTypes = (peerType: PlaycastPeerClientType): PlaycastPeerHostType[] => {
  switch (peerType) {
    case 'remoteControlClient':
      return ['remoteControlHost'];
    case 'client':
      return ['host'];
    case 'adminClient':
      return ['host'];
  }
};
export const getAcceptedHostPeerTypes = (peerType: PlaycastPeerHostType): PlaycastPeerClientType[] => {
  switch (peerType) {
    case 'remoteControlHost':
      return ['remoteControlClient'];
    case 'host':
      return ['client'];
  }
};

export const generateIdForPeer = (peerType: PlaycastPeerHostType | PlaycastPeerClientType) => {
  switch (peerType) {
    case 'remoteControlHost':
      return createRemoteHostId();
    case 'host':
      return createHostId();
    case 'remoteControlClient':
      return createRemoteControlClientId();
    case 'client':
      return createPlayerPeerId();
    case 'adminClient':
      throw new Error('Admin client peer IDs are assigned by the server, not generated locally');
  }
  throw new Error(`Invalid peer type: ${peerType}`);
};

let verbose = true;
export const setPeerLoggingVerbose = (v: boolean) => {
  verbose = v;
};

export const log = (...message: unknown[]) => {
  if (verbose) console.log('[Peer]', ...message);
};

export const debug = (...message: unknown[]) => {
  if (verbose) console.debug('[Peer]', ...message);
};

export const warn = (...message: unknown[]) => {
  console.warn('[Peer]', ...message);
};

type DisconnectHistoryRecord = {
  at: string;
  reason: string;
  context?: string;
};

const PEER_DISCONNECT_HISTORY_KEY = 'playcast.peer.disconnectHistory';

export const recordDisconnectReason = (reason: string, context?: string) => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    const now = new Date().toLocaleString();
    const raw = window.localStorage.getItem(PEER_DISCONNECT_HISTORY_KEY);
    const history: DisconnectHistoryRecord[] = raw ? JSON.parse(raw) : [];
    history.unshift({ at: now, reason, context });
    const trimmed = history.slice(0, 20);
    window.localStorage.setItem(PEER_DISCONNECT_HISTORY_KEY, JSON.stringify(trimmed));
  } catch (err) {
    // Swallow errors; diagnostics shouldn't crash app
    console.error('[Peer] Failed to record disconnect reason', err);
  }
};

export const deepCloneTrackState = (tracks: PlaycastTrackSet): PlaycastTrackSet => {
  return Object.fromEntries(
    Object.entries(tracks).map(([channelId, trackData]) => [
      channelId,
      {
        // MediaStreamTrack shouldn't be cloned, just referenced
        track: trackData.track,
        // Deep clone the metadata
        metadata: JSON.parse(JSON.stringify(trackData.metadata)),
      },
    ]),
  );
};

export const isAppleDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for macOS
  const isMac = /macintosh|mac os x/.test(userAgent);

  // Check for Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Check for iPad that identifies as MacOS in newer versions
  const isIPadOS = isMac && navigator.maxTouchPoints > 0;

  return isIOS || isMac || isIPadOS || isSafari;
};

export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for Android devices
  const isAndroid = /android/.test(userAgent);

  return isIOS || isAndroid;
};

export const captureWebrtcError = async (peerConnection: RTCPeerConnection, errorMessage: string) => {
  try {
    const statsReport = await peerConnection.getStats();
    const stats: any[] = [];
    statsReport.forEach((report) => {
      stats.push(report);
    });
    // We also want to capture the local and remote sdps
    stats.push({
      localDescription: peerConnection.localDescription,
      remoteDescription: peerConnection.remoteDescription,
    });

    // Check if Sentry is available globally
    const maybeSentry: unknown = (window as unknown as { Sentry?: unknown })?.Sentry;
    if (maybeSentry && typeof (maybeSentry as any).captureException === 'function') {
      (maybeSentry as any).captureException(new Error(errorMessage), {
        normalizeDepth: 10,
        contexts: {
          WebRTC: {
            stats,
            signalingState: peerConnection.signalingState,
            iceConnectionState: peerConnection.iceConnectionState,
            connectionState: peerConnection.connectionState,
          },
        },
      });
      console.log('[Peer] WebRTC error captured:', { errorMessage, stats });
    } else {
      console.error('[Peer] Sentry not available globally:', { errorMessage, stats });
    }
  } catch (err) {
    console.error('[Peer] Failed to capture WebRTC info:', err);
  }
};

export const isFirefoxBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('firefox');
};
